import React from 'react';
import { Todo } from '../services/goalService';
import './TodoSummary.css';
import { useTranslation } from '../i18n';
import { formatPercent } from '../utils/format';
import { getTodoProgress } from '../utils/goalProgress';
import PriorityIcon from './PriorityIcon/PriorityIcon';
import Icon from './Icon';

interface TodoSummaryProps {
  todos: Todo[];
  onUpdateTodo?: (todoId: string, data: Partial<Todo>) => void;
}

const TodoSummary: React.FC<TodoSummaryProps> = ({
  todos,
  onUpdateTodo,
}) => {
  const { t, language } = useTranslation();
  const completedTodos = todos.filter(t => t.completed).length;
  const totalTodos = todos.length;

  if (totalTodos === 0) {
    return null;
  }

  const handleTodoToggle = (todoId: string, completed: boolean) => {
    if (onUpdateTodo) {
      onUpdateTodo(todoId, { completed: !completed });
    }
  };

  const progressPercentage = getTodoProgress(todos).percent;

  // Todo'ları öncelik seviyesine göre sırala (yüksek öncelik önce)
  const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1 };
  const sortedTodos = [...todos].sort((a, b) => {
    // Tamamlanmamış todo'lar önce
    if (a.completed !== b.completed) {
      return a.completed ? 1 : -1;
    }
    // Aynı tamamlanma durumunda öncelik sırası
    return priorityOrder[b.priority] - priorityOrder[a.priority];
  });

  // Öncelik dağılımı
  const priorityCounts = {
    high: todos.filter(t => t.priority === 'high').length,
    medium: todos.filter(t => t.priority === 'medium').length,
    low: todos.filter(t => t.priority === 'low').length,
  };

  return (
    <div className="todo-summary-card">
      <div className="todo-summary-header">
        <div className="todo-count-badge">
          <span className="todo-icon"><Icon name="note" /></span>
          <span className="todo-count">
            <span className="completed-count">{completedTodos}</span>
            <span className="separator">/</span>
            <span className="total-count">{totalTodos}</span>
          </span>
          <span className="todo-label">{t('todos.summary.label')}</span>
        </div>
        <div className="header-right">
          <div className="priority-summary">
            {priorityCounts.high > 0 && (
              <span className="priority-count high"><PriorityIcon priority="high" />{priorityCounts.high}</span>
            )}
            {priorityCounts.medium > 0 && (
              <span className="priority-count medium"><PriorityIcon priority="medium" />{priorityCounts.medium}</span>
            )}
            {priorityCounts.low > 0 && (
              <span className="priority-count low"><PriorityIcon priority="low" />{priorityCounts.low}</span>
            )}
          </div>
          <div className="progress-indicator">
          <div className="progress-circle">
            <svg width="20" height="20" viewBox="0 0 24 24">
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                className="progress-bg"
              />
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                strokeDasharray={`${progressPercentage * 0.628} 62.8`}
                strokeLinecap="round"
                className="progress-fill"
                transform="rotate(-90 12 12)"
              />
            </svg>
            <span className="progress-text">{formatPercent(progressPercentage, language)}</span>
          </div>
          </div>
        </div>
      </div>
      <div className="todo-items-container">
        {sortedTodos.map(todo => (
          <button 
            key={todo.id} 
            className={`todo-item todo-item-base ${todo.completed ? 'completed' : 'pending'} ${onUpdateTodo ? 'clickable' : ''} priority-${todo.priority}`}
            onClick={() => handleTodoToggle(todo.id, todo.completed)}
            disabled={!onUpdateTodo}
          >
            <div className="todo-status">
              {todo.completed ? (
                <span className="status-icon completed-icon"><Icon name="check" /></span>
              ) : (
                <span className="status-icon pending-icon">○</span>
              )}
            </div>
            <div className="todo-content" style={{ marginBottom: "0px" }}>
              <div className="todo-title-row">
                <PriorityIcon priority={todo.priority} className={`todo-priority priority-${todo.priority}`} />
                <span className="todo-title todo-title-base">{todo.title}</span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default TodoSummary;
